import { Plus } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { FormActions, FormLayout, FormSection } from '@nucleus-commerce/admin-kit/forms';
import { useSaveBar } from '@nucleus-commerce/admin-kit/save-bar';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { knownWorkflowPaths, workflowFieldPath } from '../api/mappers';
import { useQcTemplates } from '../hooks/use-qc-templates';
import {
  emptyStage,
  MAX_STAGES,
  workflowFormSchema,
  type WorkflowFormInput,
  type WorkflowFormValues,
} from '../schemas/workflow-form-schema';

import { StageCard } from './stage-card';

export interface WorkflowTemplateFormProps {
  storeId: string;
  defaultValues: WorkflowFormInput;
  isSubmitting: boolean;
  submitLabel: string;
  readOnly?: boolean;
  onSubmit: (values: WorkflowFormValues) => Promise<unknown>;
  /** Saved forms reset to what was submitted so the save button disables again. */
  resetOnSuccess?: boolean;
  /** Opens every stage on first render, for a template being created. */
  expandInitial?: boolean;
  aside?: ReactNode;
}

/**
 * Template name and the ordered stages. Each stage collapses to a summary row with move up,
 * move down and remove; a stage with an error opens itself so the message is visible.
 */
export function WorkflowTemplateForm({
  storeId,
  defaultValues,
  isSubmitting,
  submitLabel,
  readOnly = false,
  onSubmit,
  resetOnSuccess = false,
  expandInitial = false,
  aside,
}: WorkflowTemplateFormProps) {
  const { t } = useTranslation(['workflows', 'common']);
  const message = useErrorMessage();
  const form = useForm<WorkflowFormInput, unknown, WorkflowFormValues>({
    resolver: useI18nResolver(workflowFormSchema),
    defaultValues,
  });
  const { fields, append, move, remove } = useFieldArray({ control: form.control, name: 'stages' });
  const qcTemplates = useQcTemplates(storeId);
  // Saved stages start collapsed; stages added in this session (and every stage of a new
  // template) start open. A toggle overrides either default.
  const [initialIds] = useState(() => new Set(expandInitial ? [] : fields.map((f) => f.id)));
  const [overrides, setOverrides] = useState<ReadonlyMap<string, boolean>>(() => new Map());
  const isOpen = (id: string) => overrides.get(id) ?? !initialIds.has(id);
  const rootError = form.formState.errors.root?.server?.message;
  const stagesError =
    form.formState.errors.stages?.message ?? form.formState.errors.stages?.root?.message;

  const toggle = (id: string) => setOverrides((current) => new Map(current).set(id, !isOpen(id)));

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      await onSubmit(values);
      if (resetOnSuccess) form.reset(values);
    } catch (error) {
      const applied = applyFieldErrors(error, form.setError, {
        mapField: workflowFieldPath,
        knownFields: knownWorkflowPaths(form.getValues()),
      });
      if (!applied) form.setError('root.server', { type: 'server', message: message(error) });
    }
  });
  useSaveBar({ form, onSave: submit, saving: isSubmitting, disabled: readOnly });

  return (
    <Form {...form}>
      <form onSubmit={submit} noValidate>
        <FormLayout aside={aside}>
          {rootError ? (
            <Alert variant="destructive" role="alert">
              <AlertDescription>{rootError}</AlertDescription>
            </Alert>
          ) : null}
          <FormSection title={t('workflows:editor.name')} tour="workflows.editor.name">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="sr-only">{t('workflows:editor.name')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      maxLength={120}
                      placeholder={t('workflows:editor.namePlaceholder')}
                      disabled={readOnly}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FormSection>
          <FormSection
            tour="workflows.editor.stages"
            title={t('workflows:editor.stagesTitle')}
            description={t('workflows:editor.stagesDescription')}
          >
            <ol className="flex flex-col gap-3" aria-label={t('workflows:editor.stagesTitle')}>
              {fields.map((item, index) => (
                <StageCard
                  key={item.id}
                  storeId={storeId}
                  index={index}
                  total={fields.length}
                  expanded={isOpen(item.id)}
                  onToggle={() => toggle(item.id)}
                  onMove={(to) => move(index, to)}
                  onRemove={() => remove(index)}
                  qcTemplates={qcTemplates.data ?? []}
                  disabled={readOnly}
                />
              ))}
            </ol>
            {stagesError ? (
              <p role="alert" className="text-destructive text-sm">
                {stagesError}
              </p>
            ) : null}
            {readOnly ? null : (
              <Button
                type="button"
                variant="outline"
                className="self-start"
                data-tour="workflows.editor.add-stage"
                disabled={fields.length >= MAX_STAGES}
                onClick={() => append(emptyStage())}
              >
                <Plus aria-hidden />
                {t('workflows:editor.addStage')}
              </Button>
            )}
          </FormSection>
          {readOnly ? null : (
            <FormActions
              isSubmitting={isSubmitting}
              isDirty={form.formState.isDirty}
              submitLabel={submitLabel}
              tour="workflows.editor.save"
              sticky={false}
            />
          )}
        </FormLayout>
      </form>
    </Form>
  );
}
